import React, { FC, useMemo, memo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CardActions } from "@mui/material";
import { isEmpty } from "lodash";
import { useVirtualizer } from "@tanstack/react-virtual";

import NavigationControls from "../TopNav/components/NavigationControls";
import FormSectionEditor from "./components/FormSectionEditor";
import SortQuestionsDrawer from "./components/SortQuestionsDrawer";
import EditorButtonsBottom from "./components/EditorButtonsBottom";
import EditorButtonsTop from "./components/EditorButtonsTop";
import EditSectionButtons from "./components/EditSectionButtons";
import CreateQuestionDrawer from "./components/CreateQuestionDrawer";
import AddQuestionDrawer from "./components/AddQuestionDrawer";
import DeleteSectionConfirmation from "./components/DeleteSectionConfirmation";

import {
  highlightMatch,
  Question,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { Box, Card, Skeleton, Stack, Typography } from "@components/base";
import { useGetFormSections } from "@api";
import { useUserStore } from "@store/user";

interface FormSection {}

const FormSection: FC<FormSection> = () => {
  const {
    searchQuery,
    activeSection,
    formSettings: {
      editMode,
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const { isLoadingFormSections } = useGetFormSections(user, id);

  const groupedQuestions = useMemo(() => {
    if (!activeSection || !activeSection.formQuestions) return [];
    const questions = activeSection.formQuestions;

    return questions
      .filter((question) => !question.parentQuestionId && question.isVisible)
      .map((parentQuestion, index) => {
        const subQuestions = questions
          .filter(
            (question) =>
              question.parentQuestionId === parentQuestion.questionId &&
              question.isVisible,
          )
          .map((subQuestion) => ({
            ...subQuestion,
            name: subQuestion.name,
            description: subQuestion.description || "",
          }));

        return {
          ...parentQuestion,
          questionNumber: index + 1,
          name: parentQuestion.name,
          conditions: parentQuestion.conditions,
          description: parentQuestion.description || "",
          subQuestions,
        };
      });
  }, [activeSection, searchQuery]);

  const highlightedSectionName = useMemo(
    () => highlightMatch(activeSection?.name ?? "", searchQuery),
    [activeSection, searchQuery],
  );

  const parentRef = useRef<HTMLDivElement | null>(null);
  const estimateSize = useCallback(() => 500, []);
  const rowVirtualizer = useVirtualizer({
    count: groupedQuestions.length,
    getScrollElement: () => parentRef.current,
    estimateSize,
    getItemKey: (index) => groupedQuestions[index]?.questionId ?? index,
  });

  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2,
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Stack>
              <Stack spacing={1}>
                <Typography variant="subtitle2">{highlightedSectionName}</Typography>
                <EditSectionButtons />
              </Stack>
              {activeSection?.description && (
                <Typography variant="body2" color="textSecondary">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: activeSection.description,
                    }}
                  />
                </Typography>
              )}
            </Stack>
            <EditorButtonsTop />
          </Stack>
        </Box>

        <Box flex={1} overflow="hidden" py={2} px={3}>
          <div ref={parentRef} style={{ height: "100%", overflow: "auto" }}>
            {!isLoadingFormSections ? (
              !activeSection && editMode ? (
                <Box p={3} pb={0}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    textAlign="center"
                  >
                    {t("multistep.firstCreateQuestionGroupError")}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {!isEmpty(groupedQuestions) ? (
                    <Box
                      sx={{
                        position: "relative",
                        height: rowVirtualizer.getTotalSize(),
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const q = groupedQuestions[virtualRow.index];

                        return (
                          <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              transform: `translateY(${virtualRow.start}px)`,
                              paddingBottom: 16,
                            }}
                          >
                            <Question
                              questionNumber={q.questionNumber}
                              question={q}
                              subQuestions={q.subQuestions}
                              section={activeSection}
                            />
                          </div>
                        );
                      })}
                    </Box>
                  ) : searchQuery ? (
                    <Typography variant="h5" textAlign="center">
                      {t("multistep.notFound")} {`"${searchQuery}"`}
                    </Typography>
                  ) : null}

                  <EditorButtonsBottom />
                </Stack>
              )
            ) : (
              <Stack spacing={2}>
                <Skeleton variant="text" width="100%" height={24} />
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" width="100%" height={100} />
                ))}
              </Stack>
            )}
          </div>
        </Box>

        <CardActions
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box sx={{ ml: "auto", mr: 4 }}>
            <NavigationControls />
          </Box>
        </CardActions>
      </Card>
      <CreateQuestionDrawer />
      <AddQuestionDrawer />
      <SortQuestionsDrawer />
      <FormSectionEditor />
      <DeleteSectionConfirmation />
    </>
  );
};

export default memo(FormSection);
